import { createQueryKeys } from "@lukemorales/query-key-factory";
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect, useRef } from "react";

import { api, baseApiUrl } from "@/lib/api";

import { fetchMeQueryOptions } from "./me";

export const oauths = createQueryKeys("oauths", {
  providers: null,
});

export const fetchProvidersQueryOptions = () =>
  queryOptions({
    queryKey: [oauths.providers],
    queryFn: () =>
      api
        .get<string[]>(baseApiUrl + `/oauth/providers`)
        .then((response) => response.data),
  });

export const useOauthFetchProviders = () => {
  return useQuery(fetchProvidersQueryOptions());
};

export function checkOauthCallback(success: boolean, error?: string): boolean {
  if (typeof window !== "undefined" && window.opener && !window.opener.closed) {
    window.opener.postMessage(
      {
        type: "oauth_callback",
        success,
        error,
      },
      window.location.origin,
    );
    return true;
  }

  return false;
}

export const useOauthLogin = () => {
  const queryClient = useQueryClient();
  const clenerFunction = useRef<(() => void) | null>(null);

  useEffect(() => {
    return () => clenerFunction.current?.();
  }, []);

  return useMutation({
    mutationFn: (provider: string) =>
      new Promise<void>((resolve, reject) => {
        const width = 600;
        const height = 700;
        const left = window.screen.width / 2 - width / 2;
        const top = window.screen.height / 2 - height / 2;

        const oauthUrl = `${baseApiUrl}/oauth/redirect?integration=${provider}`;

        const popup = window.open(
          oauthUrl,
          "OAuth2 Login",
          `width=${width},height=${height},top=${top},left=${left}`,
        );

        const onMessage = (event: MessageEvent) => {
          if (event.origin !== window.location.origin) return;

          if (event.data.type === "oauth_callback") {
            if (event.data.success) {
              resolve();
            } else {
              reject(new Error(event.data.error || "OAuth login failed"));
            }

            popup?.close();
            clenerFunction.current?.();

            clenerFunction.current = null;
          }
        };

        window.addEventListener("message", onMessage);

        clenerFunction.current = () => {
          window.removeEventListener("message", onMessage);
        };
      }),
    onSuccess: async () => {
      await queryClient.refetchQueries(fetchMeQueryOptions());
    },
  });
};
